// Mention autocomplete popover. Pure render — parent owns state
// (matches, active index, model-sort toggle) and the refs needed for
// blur-handling coordination with the textarea; this component just
// paints the list and wires per-row interaction.
//
// Extracted from ProjectRoomPage 2026-04-24 per Reef audit.

import type { RefObject } from "react";
import type { MentionOption, ModelSort } from "./types";

export function MentionAutocomplete({
  matches,
  activeIdx,
  channelAgentCount,
  modelSort,
  onModelSortChange,
  onActiveIdxChange,
  onPick,
  onSortBlur,
  popupRef,
  listRef,
}: {
  matches: MentionOption[];
  activeIdx: number;
  channelAgentCount: number;
  modelSort: ModelSort;
  onModelSortChange: (sort: ModelSort) => void;
  onActiveIdxChange: (idx: number) => void;
  onPick: (option: MentionOption) => void;
  onSortBlur: () => void;
  popupRef: RefObject<HTMLDivElement | null>;
  listRef: RefObject<HTMLUListElement | null>;
}) {
  if (matches.length === 0) return null;
  const modelCount = matches.filter((o) => o.kind === "model").length;

  return (
    <div
      ref={popupRef}
      className="absolute bottom-full left-6 right-6 mb-2 flex max-h-80 flex-col overflow-hidden rounded-md border border-edge bg-surface-raised shadow-[var(--wt-shadow)]"
      data-testid="mention-autocomplete"
      role="listbox"
    >
      <div className="flex items-center justify-between gap-3 border-b border-edge px-3 py-1.5 text-xs text-faint">
        <span className="whitespace-nowrap">
          {channelAgentCount} in channel · {modelCount} models
        </span>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1">
            sort
            <select
              value={modelSort}
              onChange={(e) => onModelSortChange(e.target.value as ModelSort)}
              onMouseDown={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              onBlur={(e) => {
                e.stopPropagation();
                onSortBlur();
              }}
              className="cursor-pointer rounded border border-edge bg-surface-raised px-1 py-0.5 text-xs text-primary outline-none"
              data-testid="mention-sort"
            >
              <option value="relevance">relevance</option>
              <option value="age-new">newest</option>
              <option value="age-old">oldest</option>
              <option value="price-low">cheapest</option>
              <option value="price-high">priciest</option>
            </select>
          </label>
          <span className="hidden font-code sm:inline">↑↓ · enter</span>
        </div>
      </div>
      <ul ref={listRef} className="flex flex-1 flex-col overflow-y-auto">
        {matches.map((option, idx) => {
          const active = idx === activeIdx;
          const activeTone = active
            ? "bg-accent text-[var(--wt-selection-text)]"
            : "text-primary hover:bg-surface";
          const sectionBreak =
            idx > 0 && matches[idx - 1].kind === "agent" && option.kind === "model";
          return (
            <li
              key={
                option.kind === "agent"
                  ? `agent-${option.agent.agent_id}`
                  : `model-${option.modelId}`
              }
              data-active-row={active ? "true" : undefined}
            >
              {sectionBreak && (
                <div className="border-t border-edge px-3 py-1 text-xs text-faint">
                  Models
                </div>
              )}
              <button
                type="button"
                role="option"
                aria-selected={active}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(option);
                }}
                onMouseEnter={() => onActiveIdxChange(idx)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors ${activeTone}`}
                data-testid={
                  option.kind === "agent"
                    ? `mention-option-${option.agent.seat_key}`
                    : `mention-option-${option.modelId}`
                }
              >
                {option.kind === "agent" ? (
                  <>
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate font-semibold">
                        {option.agent.display_name}
                      </span>
                      <span
                        className={`truncate text-xs ${active ? "opacity-80" : "text-muted"}`}
                      >
                        {option.agent.role_title || "Agent"}
                      </span>
                    </span>
                    <span className="shrink-0 font-code text-xs">
                      @{option.agent.seat_key}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="flex min-w-0 flex-col">
                      <span className="flex items-center gap-1.5 truncate">
                        <span className="truncate font-semibold">
                          {option.modelName}
                        </span>
                        {option.modality && (
                          <span
                            className={`shrink-0 rounded border px-1 text-[0.625rem] ${active ? "border-current opacity-80" : "border-warning text-warning"}`}
                            title="Non-text modality — won't reply with chat text"
                          >
                            {option.modality}
                          </span>
                        )}
                      </span>
                      <span
                        className={`truncate font-code text-xs ${active ? "opacity-80" : "text-muted"}`}
                      >
                        {option.modelId}
                      </span>
                    </span>
                    <span
                      className={`ml-2 flex shrink-0 flex-col items-end font-code text-xs ${active ? "opacity-80" : "text-faint"}`}
                    >
                      {option.price && (
                        <span title="Estimated cost for a typical 1000-in / 300-out turn">
                          {option.price}
                        </span>
                      )}
                      {option.age && (
                        <span title="Time since the model was released">
                          {option.age}
                        </span>
                      )}
                    </span>
                  </>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
